import { type FormEvent, useRef, useState } from 'react'
import { LOGIN_ROUTE, POST_UNLOCK_ROUTE } from '@constants/routes'
import { sessionStore } from '../services/sessionStore'
import { unlockWithMasterPassword } from '../services/unlockFlow'

/**
 * ロック解除フォーム用 state machine + onSubmit ハンドラ (T-C3)。useLoginForm / useRegisterForm
 * と同パターン。`SESSION_LIFECYCLE.md §6` の unlock flow を UI 層に提供する。
 *
 * - state 遷移 / API 呼出 / 試行カウンタ管理を本フックに集約
 * - LockPage.tsx は描画 + t(messageKey) のみ
 * - N=5 失敗で `sessionStore.clear()` + `#login` 強制遷移 (オフライン総当たり抑制)
 * - 成功時は `sessionStore.unlock(SK)` → subscribe で useSessionIdleLock が再起動 →
 *   `#home` へ navigate (空 hash は marketing landing になるため 'home' を明示)
 * - クライアント完結 — サーバ往復なし (`SESSION_LIFECYCLE.md §6` 通り)
 */

export type UnlockFormState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'unlocked' }
  | { kind: 'error'; messageKey: string; remainingAttempts: number }
  | { kind: 'logged_out'; messageKey: string }

const ERROR_MESSAGE_KEYS = {
  wrongPassword: 'auth.lock.error.wrong_password',
  noMaterial: 'auth.lock.error.no_material',
  generic: 'auth.lock.error.generic',
  exhausted: 'auth.lock.error.attempts_exhausted',
} as const

const MAX_FAILED_ATTEMPTS = 5

export interface UseUnlockFormDeps {
  unlock: typeof unlockWithMasterPassword
}

const defaultDeps: UseUnlockFormDeps = {
  unlock: unlockWithMasterPassword,
}

export interface UseUnlockForm {
  state: UnlockFormState
  isSubmitting: boolean
  /** 失敗試行残数 (初期 5、失敗ごとに減る、0 で強制 logout)。表示用に公開。 */
  remainingAttempts: number
  onSubmit: (ev: FormEvent<HTMLFormElement>) => Promise<void>
  /** "別アカウントでログイン" 用。明示的に `clear()` + `#login` へ遷移。 */
  onSwitchAccount: () => void
}

export const useUnlockForm = (deps: UseUnlockFormDeps = defaultDeps): UseUnlockForm => {
  const [state, setState] = useState<UnlockFormState>({ kind: 'idle' })
  const [remainingAttempts, setRemainingAttempts] = useState<number>(MAX_FAILED_ATTEMPTS)
  const submittingRef = useRef(false)

  const forceLogout = (messageKey: string): void => {
    sessionStore.clear()
    setState({ kind: 'logged_out', messageKey })
    window.location.hash = LOGIN_ROUTE
  }

  const onSubmit = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault()
    if (submittingRef.current) return

    const form = ev.currentTarget
    const data = new FormData(form)
    const password = String(data.get('password') ?? '')

    const material = sessionStore.getMaterial()

    if (material === null) {
      // 想定外: LOCKED 中で material が無いのは sessionStore の不整合
      forceLogout(ERROR_MESSAGE_KEYS.noMaterial)

      return
    }

    submittingRef.current = true
    setState({ kind: 'submitting' })

    try {
      const sk = await deps.unlock(password, material)

      sessionStore.unlock(sk)
      setState({ kind: 'unlocked' })
      // ログイン後と同じく #home へ。LockPage (PIN flow) と destination を
      // 共有するため POST_UNLOCK_ROUTE 定数を経由する (review I-8)。
      window.location.hash = POST_UNLOCK_ROUTE
    } catch {
      const next = remainingAttempts - 1

      setRemainingAttempts(next)
      if (next <= 0) {
        forceLogout(ERROR_MESSAGE_KEYS.exhausted)
      } else {
        setState({
          kind: 'error',
          messageKey: ERROR_MESSAGE_KEYS.wrongPassword,
          remainingAttempts: next,
        })
      }
    } finally {
      submittingRef.current = false
    }
  }

  const onSwitchAccount = (): void => {
    sessionStore.clear()
    window.location.hash = LOGIN_ROUTE
  }

  return {
    state,
    isSubmitting: state.kind === 'submitting',
    remainingAttempts,
    onSubmit,
    onSwitchAccount,
  }
}
